import { IUnitResponse } from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export const UnitDataSourceKey = 'units';

export type UnitFilter = Readonly<{
    code?: string;
    name?: string;
    nameSearch?: string;
    physicalQuantityCode?: string;
}>;

export type UseUnitsDataSourceOptions = BaseDataSourceOptions<IUnitResponse, UnitFilter>;

export type UnitsPagedDataSource = PagedDataSource<IUnitResponse, UnitFilter>;

export type UnitsInfiniteDataSource = InfiniteDataSource<IUnitResponse, UnitFilter>;

const getAllUnits: DataSourceQuery<IUnitResponse, UnitFilter> = ({
    filter: { code, name, physicalQuantityCode, nameSearch },
    sort,
    page,
    pageLimit,
}) => {
    const filterExpr = Filter.and(
        code !== undefined ? Filter.eq('Code', code) : undefined,
        name !== undefined ? Filter.eq('Name', name) : undefined,
        nameSearch !== undefined ? Filter.startsWith('Name', nameSearch) : undefined,
        physicalQuantityCode !== undefined
            ? Filter.eq('PhysicalQuantity.Code', physicalQuantityCode)
            : undefined
    );
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getUnits({
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
        })
    );
};

export function usePagedUnitsDataSource({
    ...options
}: UseUnitsDataSourceOptions): UnitsPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllUnits,
    });
}

export function useInfiniteUnitsDataSource({
    ...options
}: UseUnitsDataSourceOptions): UnitsInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllUnits,
    });
}

export function invalidateUnitsDataSource() {
    queryClient.invalidateQueries({
        queryKey: [UnitDataSourceKey],
    });
}
